import { useEffect, useRef } from 'react';
import Assets from 'imports/assets.import';
import { STRINGS } from 'constants/strings.constant';
import { useSetState } from 'utils/functions.utils';
import { IDropdownData, SortTypes } from 'utils/interface.utils';
import './search_sort_field.component.scss';

// One icon file per sort choice.
const SORT_ICONS: Record<string, string> = {
  [SortTypes.Data_Ascending]: Assets.sort_name_asc_ink,
  [SortTypes.Data_Descending]: Assets.sort_name_desc_ink,
  [SortTypes.Last_Modified]: Assets.sort_modified_ink,
};

interface SearchSortFieldProps {
  searchValue: string;
  onSearchChange: (text: string) => void;
  sortOptions: IDropdownData[];
  sortValue: IDropdownData | null;
  onSortChange: (option: IDropdownData) => void;
}

const SearchSortField = ({ searchValue, onSearchChange, sortOptions, sortValue, onSortChange }: SearchSortFieldProps): JSX.Element => {
  const strings = STRINGS.project;
  const [state, setState] = useSetState({ isMenuOpen: false });
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!state.isMenuOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent): void => {
      if (rootRef.current && event.target instanceof Node && !rootRef.current.contains(event.target)) setState({ isMenuOpen: false });
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [state.isMenuOpen, setState]);

  return (
    <div className="search_sort_field" ref={rootRef}>
      <img className="search_sort_field_icon" src={Assets.search_ink} alt="" />
      <input
        className="search_sort_field_input searchInput"
        type="text"
        placeholder={strings.searchProjects}
        aria-label={strings.searchProjects}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <button
        type="button"
        className="search_sort_field_sort"
        aria-haspopup="menu"
        aria-expanded={state.isMenuOpen}
        onClick={() => setState({ isMenuOpen: !state.isMenuOpen })}
      >
        {sortValue ? (
          <span>{strings.sort.labelWithValue} <b>{sortValue.label}</b></span>
        ) : (
          <span>{strings.sort.label}</span>
        )}
        <img className="search_sort_field_sort_chevron" src={Assets.chevron_down_ink} alt="" />
      </button>
      {state.isMenuOpen && (
        <div className="search_sort_field_menu" role="menu">
          <div className="search_sort_field_menu_heading">{strings.sort.menuHeading}</div>
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={sortValue?.value === option.value}
              className="search_sort_field_menu_item"
              onClick={() => {
                onSortChange(option);
                setState({ isMenuOpen: false });
              }}
            >
              <img className="search_sort_field_menu_icon" src={SORT_ICONS[option.value]} alt="" />
              <span>{option.label}</span>
              {sortValue?.value === option.value && (
                <img className="search_sort_field_menu_tick" src={Assets.check_ink} alt="" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchSortField;
